// BrowseExplore.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import { parseCategoryString } from '../component/FeedComponent.jsx';
import SeoHelmetWithTDK from '../component/SeoHelmetWithTDK.jsx';
import BrowseExploreJsonLd from '../component/BrowseExploreJsonLd.jsx';

import FeedComponent from '../container/FeedComponent.js';
import PullToRefresh from '../container/PullToRefresh.js';
import HomeSearchBar from '../container/HomeSearchBar.js';
import BrowseTab from '../container/BrowseTab.js';

import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import media from '../style/media.js';
import {
  footerNavigationHeight,
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';

const GAP_DEFAULT_SIZE = 16;

const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

const Gap = styled.div`
  margin-bottom: ${({ marginBottom }) =>
    marginBottom || `${GAP_DEFAULT_SIZE}px`};
`;
const CategoryRowWrapper = styled(Gap)`
  position: relative;
`;

export class BrowseExplore extends React.PureComponent {
  constructor(props) {
    super(props);
    const { categories, staticContext, fetchFeeds } = props;
    if (staticContext.actions) {
      categories
        .filter(category => !category.startsWith('pseudo_'))
        .slice(0, 5)
        .forEach(originCategory => {
          const { category } = parseCategoryString({
            category: originCategory,
          });
          staticContext.actions.push({
            function: fetchFeeds,
            argumentObject: {
              type: category,
              page: 1,
              limit: 10,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        });
    }
  }
  state = {
    shouldRefresh: false,
  };
  getRefreshProps = () => {
    const { shouldRefresh } = this.state;
    return { shouldRefresh };
  };
  handleRefresh = async ({ reset }) => {
    this.setState({
      shouldRefresh: true,
    });
    setTimeout(() => {
      reset();
      this.setState({
        shouldRefresh: false,
      });
    });
  };
  renderSEO = () => {
    const { categories, t } = this.props;
    return (
      <>
        <SeoHelmetWithTDK
          title={t('seo-explore-title', {
            ns: TranslationNamespace.SEO,
          })}
          description={t('seo-explore-description', {
            ns: TranslationNamespace.SEO,
          })}
          feeds={categories.map(category => ({
            feedId: category,
            title: t(category, {
              ns: TranslationNamespace.FEED,
            }),
          }))}
        />
        <BrowseExploreJsonLd />
        <HeadingOne>
          {t('seo-explore-h1', {
            ns: TranslationNamespace.SEO,
          })}
        </HeadingOne>
      </>
    );
  };
  render() {
    const { meId, listType, shouldRenderSearchBar, categories } = this.props;
    return (
      <StyledBrowseExplore>
        {this.renderSEO()}
        {!!meId && <BrowseTab activeTab={listType} />}
        <Body isIphoneXSeriesPWA={isIphoneXSeriesPWA}>
          {shouldRenderSearchBar && (
            <SearchBarWrapper>
              <HomeSearchBar />
            </SearchBarWrapper>
          )}
          <PullToRefresh useWindow onRefresh={this.handleRefresh}>
            <>
              {/* Rows from config */}
              {categories.map((category, index) => {
                const isLastCategory = categories.length - 1 === index;
                return (
                  <CategoryRowWrapper
                    // Avoid to use incorrect style on hydration
                    className={`categoryId_${category}`}
                    key={category}
                  >
                    <FeedComponent
                      {...this.getRefreshProps()}
                      category={category}
                      index={index}
                      isLastCategory={isLastCategory}
                    />
                  </CategoryRowWrapper>
                );
              })}
            </>
          </PullToRefresh>
        </Body>
      </StyledBrowseExplore>
    );
  }
}

BrowseExplore.propTypes = {
  meId: PropTypes.string,
  listType: PropTypes.string,
  shouldRenderSearchBar: PropTypes.bool,
  categories: PropTypes.array,
  followingCount: PropTypes.number,
  followingPageRequirement: PropTypes.number,
  staticContext: PropTypes.object,
  fetchFeeds: PropTypes.func,
  t: PropTypes.func.isRequired,
};

BrowseExplore.defaultProps = {
  meId: null,
  listType: null,
  shouldRenderSearchBar: false,
  followingCount: null,
  followingPageRequirement: 2,
  categories: [],
  staticContext: {},
  fetchFeeds: () => null,
};

const StyledBrowseExplore = styled.div`
  position: relative;
  padding-top: ${headerHeight}px;
  min-height: 100vh;
`;

const HeadingOne = styled.h1`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  font-size: 16px;
  color: #191919;
`;

const Body = styled.div`
  ${media.mobile`
    padding-bottom: ${({ isIphoneXSeriesPWA }) =>
      footerNavigationHeight +
      (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)}px;
  `};
`;

const SearchBarWrapper = styled(Gap)`
  padding: 0px 16px;
  & > div {
    display: none;
  }
  ${media.tablet`
    margin: 12px 0;
    & > div {
      display: block;
    }
  `};
  ${media.mobile`
    display: none;
  `};
`;

export default withTranslation()(BrowseExplore);
